import { Table, TableSorting } from "@/components/elements/Table";
import { useTable } from "@/hooks/useTable";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef } from "react";
import { FCC } from "@/types/common";
import { Stack, Tooltip, Typography } from "@mui/material";
import { EMPTY_STR } from "@/constants/string";
import { IconActionStar } from "@/assets/icons";
import { findLabelByValue } from "@/utils/object";
import { useEnum } from "@/api/services/global/enum";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import { TTalentListOutputDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import dayjs from "dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { CaseTalentAnotherStatus } from "@/features/case/components/CaseMatches/CaseTalentAnotherStatus";
import { useParams } from "react-router-dom";
import { text } from "@/theme/colors";

type TCaseMatchTableProps = {
  rows: TTalentListOutputDto[];
  page: number;
  setIsSelectAll: Dispatch<SetStateAction<boolean>>;
};

export const CaseMatchTable: FCC<TCaseMatchTableProps> = ({ rows, page, setIsSelectAll }) => {
  const { data: genderOptions } = useEnum("EGender");
  const { jobId } = useParams();
  const rowsRef = useRef(rows);
  const pageRef = useRef(page);
  const isSyncingSelection = useRef(false);

  const columns = useMemo<MRT_ColumnDef<TTalentListOutputDto>[]>(
    () => [
      {
        id: "talentNumber",
        header: "人材管理番号",
        Header: <TableSorting name="talent.talentNumber">人材管理番号</TableSorting>,
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.talentNumber || EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        accessorKey: "talentNumber",
      },
      {
        id: "fullName",
        header: "名前",
        Header: <TableSorting name="talent.fullName">名前</TableSorting>,
        Cell: ({ row }) => {
          const { caseMatches } = row.original;
          const isJoinCaseMatches = caseMatches.some((caseMatch) => Number(caseMatch.case?.jobId) === Number(jobId));
          return (
            <Stack direction="row" alignItems="center">
              {row.original.isRecommendedTalent && <IconActionStar />}
              {caseMatches && isJoinCaseMatches ? (
                <Tooltip title="同じ求人に過去提案歴がございます。詳細は人材情報をご確認ください。" placement="top-start">
                  <Typography variant="body2" color={text.error} sx={{ "&&": { p: 0 } }}>
                    {row.original?.fullName || EMPTY_STR.TEXT}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                  {row.original?.fullName || EMPTY_STR.TEXT}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        id: "nickName",
        header: "呼び名",
        Header: <TableSorting name="talent.nickName">呼び名</TableSorting>,
        Cell: ({ row }) => row.original.nickName,
      },
      {
        id: "isShowAnotherCase",
        header: "他提案",
        Cell: ({ row }) => {
          const { caseMatches } = row.original;
          let isAnotherStatus = false;
          if (caseMatches && caseMatches.length > 0) {
            isAnotherStatus = true;
          }
          return <CaseTalentAnotherStatus isAnotherStatus={isAnotherStatus} />;
        },
      },
      {
        id: "nationalityName",
        accessorKey: "nationality.nationalityName",
        header: "国籍",
        Header: <TableSorting name="nationality.nationalityName">国籍</TableSorting>,
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" sx={{ "&&": { p: 0 } }}>
                {row.original?.nationality?.nationalityName || EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
      },
      {
        id: "gender",
        accessorKey: "gender",
        header: "性別",
        Cell: ({ row }) => findLabelByValue(genderOptions, row.original?.gender) || EMPTY_STR.TEXT,
      },
      {
        id: "age",
        header: "年齢",
        Cell: ({ row }) => {
          const {
            original: { birthday },
          } = row;
          if (!birthday) return EMPTY_STR.TEXT;
          const today = new Date();
          const birthDate = new Date(String(birthday));

          let age = today.getFullYear() - birthDate.getFullYear();

          const hasHadBirthdayThisYear =
            today.getMonth() > birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

          if (!hasHadBirthdayThisYear) {
            age--;
          }

          return age;
        },
      },
      {
        id: "residenceStatusId",
        header: "在留資格",
        Cell: ({ row }) => row.original?.residenceStatus?.residenceStatus || EMPTY_STR.TEXT,
        Header: <TableSorting name="residenceStatus.residenceStatus">在留資格</TableSorting>,
      },
      {
        id: "periodOfStay",
        header: "在留期限",
        accessorKey: "periodOfStay",
        Cell: ({ row }) => {
          const {
            original: { periodOfStay },
          } = row;
          if (periodOfStay) {
            return dayjs(row.original?.periodOfStay).format(DATE_FORMAT);
          }
          return EMPTY_STR.DATE;
        },
        Header: <TableSorting name="talent.periodOfStay">在留期限</TableSorting>,
      },
      {
        id: "languageCertificates",
        header: "日本語能力",
        Cell: ({ row }) => row.original.languageCertificates[0]?.languageTestLevel?.level || EMPTY_STR.TEXT,
        Header: <TableSorting name="languageTestLevel.level">日本語能力</TableSorting>,
      },
      {
        id: "personInChargeId",
        header: "担当者",
        accessorKey: "personInCharge.fullName",
        Header: <TableSorting name="personInCharge.fullName">担当者</TableSorting>,
      },
    ],
    [],
  );
  const isLoading = false;

  const getCaseMatchSelected = () => {
    const raw = localStorage.getItem("case_match_selected");
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  };

  const currentPageSelectedIds = useMemo(() => {
    const dataSelected = getCaseMatchSelected();
    return (dataSelected[page] as number[]) || [];
  }, [page]);

  const { table } = useTable({
    columns,
    rows,
    enableRowSelection: true,
    getRowId: (row) => row.id.toString(),
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    state: {},
  });

  const ids = React.useMemo(() => table.getSelectedRowModel().rows.map((row) => row.original.id), [table.getSelectedRowModel()]);

  useEffect(() => {
    const prevRowIds = rowsRef.current.map((r) => r.id).sort((a, b) => a - b);
    const currentRowIds = rows.map((r) => r.id).sort((a, b) => a - b);
    const rowsChanged = prevRowIds.length !== currentRowIds.length || prevRowIds.some((id, idx) => id !== currentRowIds[idx]);
    const pageChanged = pageRef.current !== page;

    if (rowsChanged || pageChanged) {
      rowsRef.current = rows;
      pageRef.current = page;
      isSyncingSelection.current = true;

      const expectedSelection: MRT_RowSelectionState = {};
      currentPageSelectedIds.forEach((id) => {
        expectedSelection[id.toString()] = true;
      });
      table.setRowSelection(expectedSelection);

      isSyncingSelection.current = false;
    }
  }, [rows, page, currentPageSelectedIds, table]);

  useEffect(() => {
    if (isSyncingSelection.current || !page) return;

    const data = getCaseMatchSelected();
    data[page] = ids;
    localStorage.setItem("case_match_selected", JSON.stringify(data));
  }, [ids, page]);

  useEffect(() => {
    if (isSyncingSelection.current) return;
    const isSelectAll = table.getIsAllRowsSelected();
    setIsSelectAll(isSelectAll);
  }, [table.getIsAllRowsSelected(), setIsSelectAll]);

  return <Table table={table} isLoading={isLoading} maxHeight={440} />;
};
